import apiInventry from '@/utils/api-inventry';
import { handleErrorResponse, handleResponse } from '@/utils/common/common';
import {
  GetAllNotificationParam,
  GetAllNotificationResponse,
  ReadAllNotificationResponse,
  ReadNotificationParam,
  ReadNotificationResponse,
} from '@/utils/types/notification';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

// Query keys for better cache management
export const notificationQueryKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationQueryKeys.all, 'list'] as const,
  list: (params: GetAllNotificationParam) => [...notificationQueryKeys.lists(), params] as const,
};

// Get all notifications using useQuery for initial load
export const useGetAllNotification = (params: GetAllNotificationParam, enabled = true) => {
  return useQuery<GetAllNotificationResponse, Error>({
    queryKey: notificationQueryKeys.list(params),
    queryFn: async (): Promise<GetAllNotificationResponse> => {
      try {
        const response = await apiInventry.get<GetAllNotificationResponse>(`send-notification`, {
          params,
        });
        return handleResponse(response);
      } catch (error) {
        if (axios.isAxiosError(error)) throw handleErrorResponse(error);
        throw error;
      }
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Type for infinite query parameters
type InfiniteNotificationParams = Omit<GetAllNotificationParam, 'page'>;

// Infinite query for pagination on scroll
export const useGetAllNotificationInfinite = (initialParams: InfiniteNotificationParams) => {
  return useInfiniteQuery({
    queryKey: [...notificationQueryKeys.lists(), 'infinite', initialParams],
    queryFn: async ({ pageParam }: { pageParam: number }): Promise<GetAllNotificationResponse> => {
      try {
        const params: GetAllNotificationParam = { ...initialParams, page: pageParam };
        const response = await apiInventry.get<GetAllNotificationResponse>(`send-notification`, {
          params,
        });
        const result = handleResponse(response);
        return result;
      } catch (error) {
        console.error('Notification fetch error:', error);
        if (axios.isAxiosError(error)) throw handleErrorResponse(error);
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage): number | undefined => {
      const pagination = lastPage?.pagination;

      if (!pagination) {
        return undefined;
      }

      const hasNextPage = pagination.isNextPage === true;
      const currentPage = pagination.currentPage;

      // Use the API's isNextPage flag as the primary indicator
      return hasNextPage ? currentPage + 1 : undefined;
    },
    staleTime: 1000 * 60 * 2, // Reduced to 2 minutes for more frequent updates
    refetchOnWindowFocus: true, // Enable refetch on focus
    enabled: true,
  });
};

export const useReadNotification = () => {
  return useMutation<ReadNotificationResponse, Error, ReadNotificationParam>({
    mutationFn: async (params: ReadNotificationParam): Promise<ReadNotificationResponse> => {
      try {
        const response = await apiInventry.put<ReadNotificationResponse>(
          `/send-notification/single-mark-read?notificationId=${params.notificationId}`,
          {
            readStatus: true,
          },
        );
        return handleResponse(response);
      } catch (error) {
        if (axios.isAxiosError(error)) throw handleErrorResponse(error);
        throw error;
      }
    },
  });
};
export const useReadAllNotification = () => {
  return useMutation<ReadAllNotificationResponse, Error>({
    mutationFn: async (): Promise<ReadAllNotificationResponse> => {
      try {
        const response = await apiInventry.put<ReadAllNotificationResponse>(`/send-notification/mark-all-read`);
        return handleResponse(response);
      } catch (error) {
        if (axios.isAxiosError(error)) throw handleErrorResponse(error);
        throw error;
      }
    },
  });
};
